export type TeamMatchSide = 'Us' | 'Opponent';
export type TeamMatchTiebreakMethod = 'most_individual_matches_won' | 'fewest_sets_lost' | 'fewest_games_lost' | 'highest_game_winning_percentage' | 'winner_of_designated_position' | 'coin_toss' | 'governing_body_decision' | 'manual_review';

export type TeamMatchRuleset = {
  id: string;
  version: number;
  displayName: string;
  requiredPositions: string[];
  majorityRequired: number | null;
  tiebreakers: Array<{ method: TeamMatchTiebreakMethod; positionCode?: string | null }>;
  doubleDefaultTreatment?: 'no_line_win';
  determinationBasis?: 'line_wins' | 'sets_won';
};

export type TeamMatchLine = {
  positionCode: string;
  winner: TeamMatchSide | 'Unknown';
  status: 'Completed' | 'Retired' | 'Default' | 'Double Default' | 'Not Started' | 'In Progress';
  sets: Array<{ us: number | null; opponent: number | null }>;
  set3IsMatchTiebreak?: boolean;
  retired?: boolean;
};

export type TeamMatchCalculation = {
  status: 'pending' | 'winner_determined' | 'coin_toss_required' | 'governing_body_decision_required' | 'invalid_or_incomplete' | 'manual_review';
  winner: TeamMatchSide | null;
  loser: TeamMatchSide | null;
  lineWins: Record<TeamMatchSide, number>;
  sets: Record<TeamMatchSide, { won: number; lost: number }>;
  actualGames: Record<TeamMatchSide, { won: number; lost: number }>;
  adjustedGames: Record<TeamMatchSide, { won: number; lost: number }>;
  gameWinningPercentage: Record<TeamMatchSide, number | null>;
  determinationMethod: TeamMatchTiebreakMethod | 'majority' | null;
  explanation: string;
  unresolvedMethod: TeamMatchTiebreakMethod | null;
  invalidLines: string[];
  validityWarning: string | null;
};

const sides: TeamMatchSide[] = ['Us', 'Opponent'];
const other = (side: TeamMatchSide): TeamMatchSide => side === 'Us' ? 'Opponent' : 'Us';
const blankTotals = () => ({ Us: { won: 0, lost: 0 }, Opponent: { won: 0, lost: 0 } });
const percentage = (value: number) => `${(value * 100).toFixed(1)}%`;

function tiedMatchExplanation(
  winner: TeamMatchSide,
  method: TeamMatchTiebreakMethod,
  sets: TeamMatchCalculation['sets'],
  adjustedGames: TeamMatchCalculation['adjustedGames'],
  gameWinningPercentage: TeamMatchCalculation['gameWinningPercentage'],
  positionCode?: string | null,
) {
  const loser = other(winner);
  if (method === 'fewest_sets_lost') {
    return `${winner} won the 2-2 team-match tie by losing fewer sets (${sets[winner].lost} to ${sets[loser].lost}).`;
  }
  if (method === 'fewest_games_lost') {
    return `${winner} won the 2-2 team-match tie by losing fewer games (${adjustedGames[winner].lost} to ${adjustedGames[loser].lost}).`;
  }
  if (method === 'highest_game_winning_percentage') {
    return `${winner} won the 2-2 team-match tie with the higher game-winning percentage (${percentage(gameWinningPercentage[winner] || 0)} to ${percentage(gameWinningPercentage[loser] || 0)}).`;
  }
  if (method === 'winner_of_designated_position') {
    return `${winner} won the 2-2 team-match tie on the ${positionCode || 'designated'} line result.`;
  }
  return `${winner} won the tied team match by ${method.replaceAll('_', ' ')}.`;
}

function addSetAndGameTotals(line: TeamMatchLine, sets: TeamMatchCalculation['sets'], actualGames: TeamMatchCalculation['actualGames'], adjustedGames: TeamMatchCalculation['adjustedGames']) {
  const terminalWinner = line.winner === 'Us' || line.winner === 'Opponent' ? line.winner : null;
  line.sets.forEach((set, index) => {
    if (set.us == null || set.opponent == null) return;
    const isMatchTiebreak = Boolean(line.set3IsMatchTiebreak && index === 2);
    const setWinner = set.us === set.opponent ? null : set.us > set.opponent ? 'Us' : 'Opponent';
    if (setWinner) {
      sets[setWinner].won += 1;
      sets[other(setWinner)].lost += 1;
    }
    if (isMatchTiebreak) {
      actualGames.Us.won += 1; actualGames.Us.lost += 1;
      actualGames.Opponent.won += 1; actualGames.Opponent.lost += 1;
      adjustedGames.Us.won += 1; adjustedGames.Us.lost += 1;
      adjustedGames.Opponent.won += 1; adjustedGames.Opponent.lost += 1;
    } else {
      actualGames.Us.won += set.us; actualGames.Us.lost += set.opponent;
      actualGames.Opponent.won += set.opponent; actualGames.Opponent.lost += set.us;
      adjustedGames.Us.won += set.us; adjustedGames.Us.lost += set.opponent;
      adjustedGames.Opponent.won += set.opponent; adjustedGames.Opponent.lost += set.us;
    }
  });
  // USTA retirement treatment: complete the current/remaining games for the non-retiring side
  // in the calculation-only totals. The entered score remains untouched.
  if (line.status === 'Retired' && terminalWinner) {
    const winnerKey = terminalWinner === 'Us' ? 'us' : 'opponent';
    const unfinished = line.sets.find((set) => set.us != null && set.opponent != null && Math.max(set.us, set.opponent) < 6);
    if (unfinished) adjustedGames[terminalWinner].won += Math.max(0, 6 - (unfinished[winnerKey] || 0));
    const enteredSets = line.sets.filter((set) => set.us != null && set.opponent != null).length;
    const missingSets = Math.max(0, 2 - enteredSets);
    adjustedGames[terminalWinner].won += missingSets * 6;
  }
}

export function calculateTeamMatchResult(ruleset: TeamMatchRuleset | null | undefined, lines: TeamMatchLine[]): TeamMatchCalculation {
  const lineWins: Record<TeamMatchSide, number> = { Us: 0, Opponent: 0 };
  const sets = blankTotals();
  const actualGames = blankTotals();
  const adjustedGames = blankTotals();
  const invalidLines: string[] = [];
  if (!ruleset) return { status: 'manual_review', winner: null, loser: null, lineWins, sets, actualGames, adjustedGames, gameWinningPercentage: { Us: null, Opponent: null }, determinationMethod: null, explanation: 'Manual review required — no team-match ruleset is assigned.', unresolvedMethod: 'manual_review', invalidLines, validityWarning: 'Assign a ruleset before CourtCaptain can determine a team-match winner.' };

  const byPosition = new Map(lines.map((line) => [line.positionCode, line]));
  const required = ruleset.requiredPositions.length ? ruleset.requiredPositions : lines.map((line) => line.positionCode);
  required.forEach((positionCode) => {
    const line = byPosition.get(positionCode);
    if (!line || ['Not Started', 'In Progress'].includes(line.status) || line.winner === 'Unknown' && line.status !== 'Double Default') invalidLines.push(positionCode);
    if (!line) return;
    if (line.status !== 'Double Default' && (line.winner === 'Us' || line.winner === 'Opponent')) lineWins[line.winner] += 1;
    addSetAndGameTotals(line, sets, actualGames, adjustedGames);
  });
  const gameWinningPercentage: Record<TeamMatchSide, number | null> = {
    Us: adjustedGames.Us.won + adjustedGames.Us.lost ? adjustedGames.Us.won / (adjustedGames.Us.won + adjustedGames.Us.lost) : null,
    Opponent: adjustedGames.Opponent.won + adjustedGames.Opponent.lost ? adjustedGames.Opponent.won / (adjustedGames.Opponent.won + adjustedGames.Opponent.lost) : null,
  };
  const base = { lineWins, sets, actualGames, adjustedGames, gameWinningPercentage, invalidLines };
  if (invalidLines.length) return { ...base, status: 'pending', winner: null, loser: null, determinationMethod: null, explanation: `Pending — enter final results for ${invalidLines.join(', ')}.`, unresolvedMethod: null, validityWarning: null };

  if (ruleset.determinationBasis === 'sets_won') {
    if (sets.Us.won !== sets.Opponent.won) {
      const winner = sets.Us.won > sets.Opponent.won ? 'Us' : 'Opponent';
      return { ...base, status: 'winner_determined', winner, loser: other(winner), determinationMethod: 'most_individual_matches_won', explanation: `${winner} won ${sets[winner].won}-${sets[other(winner)].won} on set points.`, unresolvedMethod: null, validityWarning: null };
    }
    return { ...base, status: 'manual_review', winner: null, loser: null, determinationMethod: null, explanation: `Tied ${sets.Us.won}-${sets.Opponent.won} on set points. Local TCD review is required.`, unresolvedMethod: 'manual_review', validityWarning: null };
  }

  const majority = ruleset.majorityRequired ?? Math.floor(required.length / 2) + 1;
  for (const side of sides) if (lineWins[side] >= majority) return { ...base, status: 'winner_determined', winner: side, loser: other(side), determinationMethod: 'majority', explanation: `${side} won ${lineWins[side]} of ${required.length} lines.`, unresolvedMethod: null, validityWarning: required.some((positionCode) => byPosition.get(positionCode)?.status === 'Double Default') ? 'A double default may require local-league validity review.' : null };

  for (const rule of ruleset.tiebreakers) {
    let winner: TeamMatchSide | null = null;
    if (rule.method === 'most_individual_matches_won' && lineWins.Us !== lineWins.Opponent) winner = lineWins.Us > lineWins.Opponent ? 'Us' : 'Opponent';
    if (rule.method === 'fewest_sets_lost' && sets.Us.lost !== sets.Opponent.lost) winner = sets.Us.lost < sets.Opponent.lost ? 'Us' : 'Opponent';
    if (rule.method === 'fewest_games_lost' && adjustedGames.Us.lost !== adjustedGames.Opponent.lost) winner = adjustedGames.Us.lost < adjustedGames.Opponent.lost ? 'Us' : 'Opponent';
    if (rule.method === 'highest_game_winning_percentage' && gameWinningPercentage.Us != null && gameWinningPercentage.Opponent != null && gameWinningPercentage.Us !== gameWinningPercentage.Opponent) winner = gameWinningPercentage.Us > gameWinningPercentage.Opponent ? 'Us' : 'Opponent';
    if (rule.method === 'winner_of_designated_position') {
      const line = rule.positionCode ? byPosition.get(rule.positionCode) : null;
      if (line?.winner === 'Us' || line?.winner === 'Opponent') winner = line.winner;
    }
    if (winner) return {
      ...base,
      status: 'winner_determined',
      winner,
      loser: other(winner),
      determinationMethod: rule.method,
      explanation: tiedMatchExplanation(winner, rule.method, sets, adjustedGames, gameWinningPercentage, rule.positionCode),
      unresolvedMethod: null,
      validityWarning: null,
    };
    if (rule.method === 'coin_toss') return { ...base, status: 'coin_toss_required', winner: null, loser: null, determinationMethod: null, explanation: 'Coin toss required to resolve the tied team match.', unresolvedMethod: 'coin_toss', validityWarning: null };
    if (rule.method === 'governing_body_decision') return { ...base, status: 'governing_body_decision_required', winner: null, loser: null, determinationMethod: null, explanation: 'Governing-body decision required to resolve the tied team match.', unresolvedMethod: 'governing_body_decision', validityWarning: null };
    if (rule.method === 'manual_review') return { ...base, status: 'manual_review', winner: null, loser: null, determinationMethod: null, explanation: 'Manual review required to resolve the tied team match.', unresolvedMethod: 'manual_review', validityWarning: null };
  }
  return { ...base, status: 'manual_review', winner: null, loser: null, determinationMethod: null, explanation: 'Manual review required — the tied team match has no resolving rule.', unresolvedMethod: 'manual_review', validityWarning: null };
}

/** Applies the captain-recorded outcome only after the configured rules reach coin toss. */
export function applyCoinTossResult(calculation: TeamMatchCalculation, winner: TeamMatchSide | null): TeamMatchCalculation {
  if (calculation.status !== 'coin_toss_required' || !winner) return calculation;
  return {
    ...calculation,
    status: 'winner_determined',
    winner,
    loser: other(winner),
    determinationMethod: 'coin_toss',
    explanation: `${winner} won the tied team match by coin toss.`,
    unresolvedMethod: null,
    validityWarning: null,
  };
}

export function courtCaptainRulesetForPositions(positions: Array<{ position_code: string }>, isTcdMatch = false): TeamMatchRuleset {
  const requiredPositions = positions.map((position) => position.position_code);
  const count = requiredPositions.length;
  if (isTcdMatch) return { id: 'tcd-four-line-set-points', version: 1, displayName: 'TCD Four-Line Set Points', requiredPositions, majorityRequired: null, tiebreakers: [], determinationBasis: 'sets_won' };
  if (count === 4) return { id: 'court-captain-four-line-default', version: 1, displayName: 'USTA Local Four-Line — CourtCaptain Default', requiredPositions, majorityRequired: 3, tiebreakers: [{ method: 'fewest_sets_lost' }, { method: 'fewest_games_lost' }, { method: 'highest_game_winning_percentage' }, { method: 'winner_of_designated_position', positionCode: 'D1' }] };
  return { id: count === 3 ? 'usta-standard-three-line-majority' : 'usta-standard-five-line-majority', version: 1, displayName: count === 3 ? 'USTA Standard Three-Line Majority' : 'USTA Standard Five-Line Majority', requiredPositions, majorityRequired: Math.floor(count / 2) + 1, tiebreakers: [] };
}
